import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CreditCard, Check, Plus, Trash2 } from 'lucide-react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { cardService } from '@/services/cardService';
import { toast } from 'sonner';
import { CardBrandIcon } from './CardBrandIcon';
import { cn } from '@/lib/utils';
import { PaymentMethod } from '@/hooks/usePaymentMethods';

interface PaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'edit' | 'select';
  onSave: (data: any) => void;
  existingCards?: PaymentMethod[];
  selectedCard?: PaymentMethod | null;
  onCardSelect?: (card: PaymentMethod) => void;
  onCardDelete?: (cardId: string) => Promise<void>;
  onRefreshCards?: () => Promise<void>;
}

export function PaymentMethodModal({
  open,
  onOpenChange,
  mode,
  onSave,
  existingCards = [],
  selectedCard,
  onCardSelect,
  onCardDelete,
  onRefreshCards,
}: PaymentMethodModalProps) {
  useLanguage();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useCustomAuth();

  const [cardHolder, setCardHolder] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'select' | 'add'>(mode === 'select' ? 'select' : 'add');
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  // Resetar viewMode quando o modal abrir ou o mode mudar
  useEffect(() => {
    if (open) {
      setViewMode(mode === 'select' ? 'select' : 'add');
      setCardHolder('');
      // Se não houver cartões cadastrados, o novo já será o padrão por consequência
      setSetAsDefault(existingCards.length === 0);
    }
  }, [open, mode, existingCards.length]);

  const handleCardSelection = (card: PaymentMethod) => {
    if (onCardSelect) {
      onCardSelect(card);
      onOpenChange(false);
    }
  };

  const handleDeleteCard = async (cardId: string, cardLast4: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Impede que o click no botão delete selecione o cartão

    const confirmed = window.confirm(
      `Tem certeza que deseja remover o cartão final ${cardLast4}?`
    );

    if (!confirmed) return;

    setDeletingCardId(cardId);

    if (onCardDelete) {
      await onCardDelete(cardId);
    }

    if (onRefreshCards) {
      await onRefreshCards();
    }

    setDeletingCardId(null);
  };

  const handleSave = async () => {
    if (!stripe || !elements) {
      toast.error(translateInterfaceText("Stripe não está carregado"));
      return;
    }

    if (!user?.id_Stripe_Client) {
      toast.error(translateInterfaceText("Usuário não encontrado"));
      return;
    }

    if (!cardHolder.trim()) {
      toast.error(translateInterfaceText("Digite o nome impresso no cartão"));
      return;
    }

    setIsLoading(true);

    try {
      // Get card element
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        toast.error(translateInterfaceText("Erro ao processar cartão"));
        setIsLoading(false);
        return;
      }

      // Create token
      const result = await stripe.createToken(cardNumberElement, {
        name: cardHolder,
      });

      if (result.error) {
        toast.error('Erro ao processar cartão: ' + result.error.message);
        setIsLoading(false);
        return;
      }

      // Create card via API
      const response = await cardService.createCard(user.id_Stripe_Client, {
        token: result.token.id,
      });

      if (response.success === false) {
        // Handle card errors
        const errorMessages: Record<string, string> = {
          'Your card was declined.': translateInterfaceText("Seu cartão foi recusado, tente novamente."),
          'Your card has insufficient funds.': translateInterfaceText("Seu cartão não possui saldo suficiente."),
          'Your card has expired.': translateInterfaceText("Seu cartão está vencido."),
          "Your card's security code is incorrect.": translateInterfaceText("O código de segurança está incorreto."),
        };

        const message = errorMessages[response.message] || response.message || translateInterfaceText("Erro ao processar pagamento.");
        toast.error(message);
        setIsLoading(false);
        return;
      }

      // Se o usuário marcou como padrão, atualiza o default no backend
      if (setAsDefault && response.data?.id_Stripe_Card) {
        const defaultResp = await cardService.setDefaultCard(user.id_Stripe_Client, {
          cardId: response.data.id_Stripe_Card,
        });
        if (defaultResp?.success === false) {
          toast.error(translateInterfaceText("Cartão adicionado, mas não foi possível defini-lo como padrão."));
        }
      }

      // Success
      toast.success(translateInterfaceText("Cartão adicionado com sucesso!"));
      onSave(response.data);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Erro ao salvar cartão:', error);
      toast.error(translateInterfaceText("Erro inesperado. Tente novamente."));
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPortal>
        <DialogOverlay
          className="!z-[10000]"
          onClick={() => onOpenChange(false)}
        />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-[50%] top-[50%] z-[10001] translate-x-[-50%] translate-y-[-50%] w-[92%] max-w-[360px] sm:w-[420px] sm:max-w-[420px] max-h-[90vh] overflow-y-auto p-4 sm:p-5 rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] bg-white border-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200 dark:bg-[rgb(var(--modal-bg))] dark:border-[rgb(var(--modal-border))]"
        >
          <div className="w-full flex flex-col items-start text-left space-y-1">
            <div className="flex items-center gap-[10px]">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#437DFF]/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-[#437DFF]" />
              </div>
              <DialogPrimitive.Title asChild>
                <h2 className="text-[18px] sm:text-lg font-semibold text-[#1E293B] whitespace-nowrap overflow-hidden text-ellipsis">
                  {viewMode === 'select' ? translateInterfaceText("Selecionar Cartão") : mode === 'add' ? translateInterfaceText("Adicionar pagamento") : translateInterfaceText("Editar pagamento")}
                </h2>
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Description asChild>
              <p className="text-[13px] text-[#64748B] leading-relaxed">
                {viewMode === 'select'
                  ? translateInterfaceText("Escolha um cartão ou adicione um novo.")
                  : mode === 'add'
                  ? translateInterfaceText("Digite as informações do cartão.")
                  : translateInterfaceText("Atualize as informações do cartão.")}
              </p>
            </DialogPrimitive.Description>
          </div>

        {viewMode === 'select' ? (
          /* Seleção de Cartões */
          <div className="w-full space-y-3 mt-4">
            {existingCards.length > 0 ? (
              <>
                <div className="space-y-2">
                  {existingCards.map((card) => (
                    <div
                      key={card.id}
                      className={cn(
                        "relative w-full p-3 rounded-[10px] border transition-all flex items-center gap-3 group",
                        selectedCard?.id === card.id
                          ? "border-[#437DFF] bg-[#437DFF]/5"
                          : "border-[#E2E8F0] hover:border-[#437DFF]/50 hover:bg-gray-50"
                      )}
                    >
                      <button
                        onClick={() => handleCardSelection(card)}
                        className="flex items-center gap-3 flex-1"
                      >
                        <CardBrandIcon brand={card.brand || 'unknown'} className="w-10 h-7 flex-shrink-0" />
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <p className="text-[14px] font-medium text-[#111827]">
                              {card.brand || translateInterfaceText("Cartão")} •••• {card.last4}
                            </p>
                            {card.is_default && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-[6px] bg-[#437DFF]/10 text-[#437DFF]">
                                {translateInterfaceText("Padrão")}</span>
                            )}
                          </div>
                          <p className="text-[12px] text-[#6B7280]">
                            {translateInterfaceText("Expira em")}{" "}{String(card.expiry_month).padStart(2, '0')}/{String(card.expiry_year).slice(-2)}
                          </p>
                        </div>
                        {card.is_default && (
                          <div className="w-5 h-5 rounded-full bg-[#437DFF] flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>

                      {/* Botão de deletar */}
                      <button
                        onClick={(e) => handleDeleteCard(card.id_stripe, card.last4, e)}
                        disabled={deletingCardId === card.id_stripe}
                        className="p-2 rounded-lg text-[#6B7280] hover:text-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={translateInterfaceText("Remover cartão")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setViewMode('add')}
                  className="w-full p-3 rounded-[10px] border border-dashed border-[#437DFF] text-[#437DFF] hover:bg-[#437DFF]/5 transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[14px] font-medium">{translateInterfaceText("Adicionar novo cartão")}</span>
                </button>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-[14px] text-[#6B7280] mb-4">{translateInterfaceText("Nenhum cartão cadastrado")}</p>
                <Button
                  onClick={() => setViewMode('add')}
                  className="h-[44px] px-6 rounded-[10px] bg-[#437DFF] hover:bg-[#355FCC] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {translateInterfaceText("Adicionar Cartão")}</Button>
              </div>
            )}
          </div>
        ) : (
          /* Formulário de Adicionar Cartão */
          <div className="w-full space-y-3 mt-4">
            {/* Número do Cartão */}
            <div className="space-y-2">
              <Label htmlFor="cardNumber" className="text-[13px] font-semibold text-[#1E293B]">
                {translateInterfaceText("Número do Cartão")}</Label>
              <div className="h-[40px] px-3 py-3 rounded-[10px] border border-[#E2E8F0] focus-within:border-[#437DFF] focus-within:ring-1 focus-within:ring-[#437DFF]">
                <CardNumberElement
                  options={{
                    style: {
                      base: {
                        fontSize: '13px',
                        color: '#1E293B',
                        lineHeight: '20px',
                        '::placeholder': {
                          color: '#94A3B8',
                        },
                      },
                      invalid: {
                        color: '#EF4444',
                      },
                    },
                    placeholder: '1234 1234 1234 1234',
                  }}
                />
              </div>
            </div>

            {/* Nome impresso no cartão */}
            <div className="space-y-2">
              <Label htmlFor="cardHolder" className="text-[13px] font-semibold text-[#1E293B]">
                {translateInterfaceText("Nome impresso no cartão")}</Label>
              <Input
                id="cardHolder"
                placeholder={translateInterfaceText("Digite o nome impresso no cartão")}
                value={cardHolder}
                onChange={(e) => setCardHolder(e.target.value)}
                className="h-[40px] rounded-[10px] border border-[#E2E8F0] focus:border-[#437DFF] focus:ring-1 focus:ring-[#437DFF]"
              />
            </div>

            {/* Validade e CVC */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiry" className="text-[13px] font-semibold text-[#1E293B]">
                  {translateInterfaceText("Validade")}</Label>
                <div className="h-[40px] px-3 py-3 rounded-[10px] border border-[#E2E8F0] focus-within:border-[#437DFF] focus-within:ring-1 focus-within:ring-[#437DFF]">
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: '13px',
                          color: '#1E293B',
                          lineHeight: '20px',
                          '::placeholder': {
                            color: '#94A3B8',
                          },
                        },
                        invalid: {
                          color: '#EF4444',
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvc" className="text-[13px] font-semibold text-[#1E293B]">
                  {translateInterfaceText("CVC")}</Label>
                <div className="h-[40px] px-3 py-3 rounded-[10px] border border-[#E2E8F0] focus-within:border-[#437DFF] focus-within:ring-1 focus-within:ring-[#437DFF]">
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: '13px',
                          color: '#1E293B',
                          lineHeight: '20px',
                          '::placeholder': {
                            color: '#94A3B8',
                          },
                        },
                        invalid: {
                          color: '#EF4444',
                        },
                      },
                      placeholder: '123',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Definir como padrão */}
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="flex flex-col">
                <Label htmlFor="setAsDefault" className="text-[13px] font-semibold text-[#1E293B] cursor-pointer">
                  {translateInterfaceText("Definir como cartão padrão")}</Label>
                <p className="text-[11px] text-[#64748B] leading-relaxed">
                  {translateInterfaceText("Usaremos este cartão para cobranças futuras.")}</p>
              </div>
              <Switch
                id="setAsDefault"
                checked={setAsDefault}
                onCheckedChange={setSetAsDefault}
                disabled={existingCards.length === 0}
              />
            </div>
          </div>
        )}

        {/* Botões */}
        {viewMode === 'select' && existingCards.length > 0 ? (
          <div className="w-full flex flex-row gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-[44px] rounded-[10px] border border-[#E5E7EB] bg-white text-[#374151] text-[14px] font-semibold"
            >
              {translateInterfaceText("Cancelar")}</Button>
          </div>
        ) : viewMode === 'add' ? (
          <div className="w-full flex flex-row gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                if (mode === 'select' && existingCards.length > 0) {
                  setViewMode('select');
                } else {
                  onOpenChange(false);
                }
              }}
              disabled={isLoading}
              className="flex-1 h-[44px] rounded-[10px] border border-[#E5E7EB] bg-white text-[#374151] text-[14px] font-semibold"
            >
              {mode === 'select' && existingCards.length > 0 ? translateInterfaceText("Voltar") : translateInterfaceText("Cancelar")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!stripe || isLoading}
              className="flex-1 h-[44px] rounded-[10px] bg-[#437DFF] hover:bg-[#355FCC] text-white text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? translateInterfaceText("Processando...") : translateInterfaceText("Adicionar Cartão")}
            </Button>
          </div>
        ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
